/**
 * Criticality Matrix / FMECA Criticality Computation Engine
 * Implements MIL-STD-1629A criticality analysis for OptimalAvailability Studio
 *
 * This engine provides:
 * - MIL-STD-1629A criticality number calculations
 * - Criticality matrix (severity vs probability)
 * - Risk priority number (RPN) calculations
 * - Criticality ranking and distribution
 * - Risk assessment and mitigation prioritization
 * - 4-axis criticality scoring (Safety, Operational, Environmental, Cost) matching Isograph AWB
 * - Visualization data for charts and matrices
 */

export class CriticalityEngine {
  /**
   * 4-Axis Criticality Dimensions (Isograph AWB equivalent)
   * Each failure mode/cause has a score on each axis (1-5 scale)
   * @type {Object}
   */
  static CRITICALITY_AXES = {
    S: { name: 'Safety', description: 'Impact on personnel safety and system integrity', scale: '1-5' },
    O: { name: 'Operational', description: 'Impact on system operational capability and availability', scale: '1-5' },
    E: { name: 'Environmental', description: 'Environmental impact and regulatory compliance', scale: '1-5' },
    C: { name: 'Cost', description: 'Direct and indirect cost impact', scale: '1-5' }
  };

  /**
   * Severity levels per MIL-STD-1629A
   * @type {Object}
   */
  static SEVERITY_LEVELS = {
    I: { name: 'Catastrophic', order: 1, value: 4 },
    II: { name: 'Critical', order: 2, value: 3 },
    III: { name: 'Marginal', order: 3, value: 2 },
    IV: { name: 'Negligible', order: 4, value: 1 }
  };

  /**
   * Probability levels per MIL-STD-1629A
   * @type {Object}
   */
  static PROBABILITY_LEVELS = {
    A: { name: 'Frequent', order: 1, value: 5, range: '> 0.1' },
    B: { name: 'Reasonably Probable', order: 2, value: 4, range: '0.01 - 0.1' },
    C: { name: 'Occasional', order: 3, value: 3, range: '0.001 - 0.01' },
    D: { name: 'Remote', order: 4, value: 2, range: '0.0001 - 0.001' },
    E: { name: 'Extremely Unlikely', order: 5, value: 1, range: '< 0.0001' }
  };

  /**
   * Risk assessment matrix colors and zones
   * Row: Severity (I-IV), Column: Probability (A-E)
   * @type {Object}
   */
  static RISK_MATRIX = {
    I: { A: 'red', B: 'red', C: 'orange', D: 'orange', E: 'yellow' },
    II: { A: 'red', B: 'orange', C: 'orange', D: 'yellow', E: 'yellow' },
    III: { A: 'orange', B: 'orange', C: 'yellow', D: 'yellow', E: 'green' },
    IV: { A: 'yellow', B: 'yellow', C: 'yellow', D: 'green', E: 'green' }
  };

  /**
   * Risk zone labels and action items
   * @type {Object}
   */
  static RISK_ZONES = {
    red: { label: 'Unacceptable', action: 'Eliminate or mitigate immediately' },
    orange: { label: 'Undesirable', action: 'Require mitigation or control measures' },
    yellow: { label: 'Acceptable with Review', action: 'Monitor and implement controls' },
    green: { label: 'Acceptable', action: 'No action required' }
  };

  /**
   * Calculate modal criticality number per MIL-STD-1629A
   * Cm = α × β × λp × t
   *
   * @param {number} alpha - Failure mode ratio (0-1), portion of item failure rate for this mode
   * @param {number} beta - Conditional probability of loss / severity factor (0-1)
   * @param {number} lambda_p - Part failure rate (failures per unit time)
   * @param {number} t - Operating time / mission duration (hours)
   * @returns {number} Modal criticality number
   */
  calculateModalCriticality(alpha, beta, lambda_p, t) {
    if (!this._validateInputs(alpha, beta, lambda_p, t)) {
      return 0;
    }
    return alpha * beta * lambda_p * t;
  }

  /**
   * Calculate item criticality number (sum of all modal criticalities)
   * Cr = Σ(Cm_j) for all failure modes j
   *
   * @param {number[]} cmValues - Array of modal criticality numbers
   * @returns {number} Item criticality number
   */
  calculateItemCriticality(cmValues) {
    if (!Array.isArray(cmValues)) {
      return 0;
    }
    return cmValues.reduce((sum, cm) => sum + (typeof cm === 'number' ? cm : 0), 0);
  }

  /**
   * Calculate Risk Priority Number (RPN)
   * RPN = Severity × Occurrence × Detection
   *
   * @param {number} severity - Severity rating (1-10)
   * @param {number} occurrence - Occurrence probability rating (1-10)
   * @param {number} detection - Detection difficulty rating (1-10)
   * @returns {number} Risk Priority Number
   */
  calculateRPN(severity, occurrence, detection) {
    const s = Math.max(1, Math.min(10, severity || 0));
    const o = Math.max(1, Math.min(10, occurrence || 0));
    const d = Math.max(1, Math.min(10, detection || 0));
    return s * o * d;
  }

  /**
   * Build criticality matrix with severity vs probability
   *
   * @param {Object[]} failureModes - Array of failure mode objects
   * @param {string} failureModes[].id - Failure mode identifier
   * @param {string} failureModes[].description - Mode description
   * @param {string} failureModes[].severity - Severity level (I-IV)
   * @param {string} failureModes[].probability - Probability level (A-E)
   * @returns {Object} Matrix with cell counts and organization
   */
  buildCriticalityMatrix(failureModes = []) {
    const matrix = {};

    // Initialize matrix cells
    Object.keys(CriticalityEngine.SEVERITY_LEVELS).forEach(severity => {
      matrix[severity] = {};
      Object.keys(CriticalityEngine.PROBABILITY_LEVELS).forEach(probability => {
        matrix[severity][probability] = {
          count: 0,
          modes: [],
          color: CriticalityEngine.RISK_MATRIX[severity][probability],
          riskZone: this._getRiskZone(CriticalityEngine.RISK_MATRIX[severity][probability])
        };
      });
    });

    // Populate with failure modes
    failureModes.forEach(mode => {
      const severity = mode.severity || 'IV';
      const probability = mode.probability || 'E';

      if (matrix[severity] && matrix[severity][probability]) {
        matrix[severity][probability].count += 1;
        matrix[severity][probability].modes.push(mode.id || mode.description);
      }
    });

    return matrix;
  }

  /**
   * Get risk level for a specific matrix cell
   *
   * @param {string} severity - Severity level (I-IV)
   * @param {string} probability - Probability level (A-E)
   * @returns {Object} Risk level with color and zone information
   */
  getCellRiskLevel(severity, probability) {
    if (!CriticalityEngine.SEVERITY_LEVELS[severity] ||
        !CriticalityEngine.PROBABILITY_LEVELS[probability]) {
      return { color: 'gray', zone: 'Unknown', action: 'Invalid parameters' };
    }

    const color = CriticalityEngine.RISK_MATRIX[severity][probability];
    const zone = CriticalityEngine.RISK_ZONES[color];

    return {
      severity,
      probability,
      color,
      zone: zone.label,
      action: zone.action
    };
  }

  /**
   * Rank failure modes by RPN in descending order
   *
   * @param {Object[]} failureModes - Array of failure mode objects with severity, occurrence, detection
   * @returns {Object[]} Sorted array with RPN calculations
   */
  rankByRPN(failureModes = []) {
    return failureModes
      .map(mode => ({
        ...mode,
        rpn: this.calculateRPN(mode.severity, mode.occurrence, mode.detection)
      }))
      .sort((a, b) => b.rpn - a.rpn);
  }

  /**
   * Get Pareto curve data (cumulative RPN contribution)
   *
   * @param {Object[]} failureModes - Array of failure mode objects
   * @returns {Object} Pareto data with modes and cumulative percentages
   */
  getParetoCurveData(failureModes = []) {
    const ranked = this.rankByRPN(failureModes);
    const totalRPN = ranked.reduce((sum, mode) => sum + mode.rpn, 0);

    if (totalRPN === 0) {
      return { modes: [], cumulativePercentages: [], totalRPN: 0 };
    }

    let cumulative = 0;
    const paretoPairs = ranked.map((mode, index) => {
      cumulative += mode.rpn;
      return {
        index: index + 1,
        description: mode.description || `Mode ${index + 1}`,
        rpn: mode.rpn,
        percentage: (mode.rpn / totalRPN) * 100,
        cumulativePercentage: (cumulative / totalRPN) * 100
      };
    });

    return {
      modes: paretoPairs,
      totalRPN,
      eightyPercentThreshold: paretoPairs.find(p => p.cumulativePercentage >= 80)
    };
  }

  /**
   * Rank items by criticality number in descending order
   *
   * @param {Object[]} items - Array of items with criticality data
   * @returns {Object[]} Sorted array by criticality number
   */
  rankByCriticality(items = []) {
    return [...items].sort((a, b) => (b.criticality || 0) - (a.criticality || 0));
  }

  /**
   * Get top N most critical items
   *
   * @param {Object[]} items - Array of items with criticality data
   * @param {number} n - Number of top items to return
   * @returns {Object[]} Top N critical items
   */
  getTopContributors(items = [], n = 10) {
    return this.rankByCriticality(items).slice(0, n);
  }

  /**
   * Get criticality distribution for histogram visualization
   *
   * @param {Object[]} items - Array of items with criticality values
   * @param {number} binCount - Number of histogram bins (default 10)
   * @returns {Object} Distribution data with bins
   */
  getCriticalityDistribution(items = [], binCount = 10) {
    if (items.length === 0) {
      return { bins: [], minValue: 0, maxValue: 0, mean: 0, median: 0 };
    }

    const criticalities = items
      .map(item => item.criticality || 0)
      .filter(c => c >= 0);

    if (criticalities.length === 0) {
      return { bins: [], minValue: 0, maxValue: 0, mean: 0, median: 0 };
    }

    const minValue = Math.min(...criticalities);
    const maxValue = Math.max(...criticalities);
    const range = maxValue - minValue || 1;
    const binSize = range / binCount;

    // Create bins
    const bins = Array(binCount).fill(0).map((_, i) => ({
      index: i,
      rangeStart: minValue + (i * binSize),
      rangeEnd: minValue + ((i + 1) * binSize),
      count: 0
    }));

    // Populate bins
    criticalities.forEach(value => {
      const binIndex = Math.floor((value - minValue) / binSize);
      const idx = Math.min(binIndex, binCount - 1);
      if (idx >= 0) {
        bins[idx].count += 1;
      }
    });

    // Calculate statistics
    const mean = criticalities.reduce((a, b) => a + b, 0) / criticalities.length;
    const sorted = [...criticalities].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];

    return { bins, minValue, maxValue, mean, median, count: criticalities.length };
  }

  /**
   * Assess risk based on severity and probability
   *
   * @param {string} severity - Severity level (I-IV)
   * @param {string} probability - Probability level (A-E)
   * @returns {Object} Risk assessment with level, color, and action
   */
  assessRisk(severity, probability) {
    return this.getCellRiskLevel(severity, probability);
  }

  /**
   * Get prioritized list of failure modes requiring mitigation
   *
   * @param {Object[]} failureModes - Array of failure modes
   * @returns {Object[]} Sorted by mitigation priority (RPN-based)
   */
  getMitigationPriority(failureModes = []) {
    return this.rankByRPN(failureModes)
      .filter(mode => {
        const riskLevel = this.getCellRiskLevel(mode.severity, mode.probability);
        return riskLevel.color !== 'green';
      })
      .map((mode, index) => ({
        ...mode,
        priority: index + 1,
        action: this.getCellRiskLevel(mode.severity, mode.probability).action
      }));
  }

  /**
   * Calculate residual risk after mitigation
   *
   * @param {Object} failureMode - Failure mode object
   * @param {number} mitigationEffectiveness - Effectiveness factor (0-1, where 1 = eliminates risk)
   * @returns {Object} Residual risk assessment
   */
  calculateResidualRisk(failureMode = {}, mitigationEffectiveness = 0) {
    const effectiveness = Math.max(0, Math.min(1, mitigationEffectiveness));

    const originalRPN = this.calculateRPN(
      failureMode.severity,
      failureMode.occurrence,
      failureMode.detection
    );

    const residualRPN = Math.round(originalRPN * (1 - effectiveness));

    return {
      originalRPN,
      mitigationEffectiveness: effectiveness * 100,
      residualRPN,
      reduction: originalRPN - residualRPN,
      reductionPercentage: ((originalRPN - residualRPN) / originalRPN * 100) || 0,
      isAcceptable: residualRPN <= 100
    };
  }

  /**
   * Get matrix data for visualization rendering
   * Returns a structured representation of the 5×4 criticality matrix
   *
   * @returns {Object} Matrix with cell data, colors, and counts
   */
  getMatrixData(failureModes = []) {
    const matrix = this.buildCriticalityMatrix(failureModes);

    const rows = Object.entries(CriticalityEngine.SEVERITY_LEVELS)
      .map(([key, level]) => ({
        key,
        ...level,
        cells: Object.entries(CriticalityEngine.PROBABILITY_LEVELS).map(([probKey, probLevel]) => ({
          severity: key,
          probability: probKey,
          ...matrix[key][probKey],
          severityLabel: level.name,
          probabilityLabel: probLevel.name
        }))
      }));

    return {
      rows,
      columns: Object.entries(CriticalityEngine.PROBABILITY_LEVELS).map(([key, level]) => ({
        key,
        ...level
      })),
      totalModes: failureModes.length
    };
  }

  /**
   * Get Pareto plot data for visualization
   *
   * @param {Object[]} failureModes - Array of failure modes
   * @returns {Object} Pareto chart data structure
   */
  getParetoPlotData(failureModes = []) {
    const paretoData = this.getParetoCurveData(failureModes);

    return {
      chartData: {
        labels: paretoData.modes.map(m => m.description),
        rpnValues: paretoData.modes.map(m => m.rpn),
        cumulativePercentages: paretoData.modes.map(m => m.cumulativePercentage)
      },
      summary: {
        totalModes: paretoData.modes.length,
        totalRPN: paretoData.totalRPN,
        eightyPercentCount: paretoData.eightyPercentThreshold?.index || 0,
        eightyPercentPercentage: paretoData.eightyPercentThreshold?.cumulativePercentage || 0
      }
    };
  }

  /**
   * Get criticality bar chart data
   *
   * @param {Object[]} items - Array of items with criticality data
   * @param {number} limit - Maximum number of items to display
   * @returns {Object} Bar chart data structure
   */
  getCriticalityBarData(items = [], limit = 20) {
    const ranked = this.rankByCriticality(items).slice(0, limit);

    return {
      labels: ranked.map((item, idx) => item.name || `Item ${idx + 1}`),
      values: ranked.map(item => item.criticality || 0),
      colors: ranked.map(item => this._getCriticalityColor(item.criticality || 0)),
      items: ranked
    };
  }

  /**
   * Validate numeric inputs for calculations
   * @private
   */
  _validateInputs(...values) {
    return values.every(v => typeof v === 'number' && v >= 0 && isFinite(v));
  }

  /**
   * Get risk zone label from color
   * @private
   */
  _getRiskZone(color) {
    return CriticalityEngine.RISK_ZONES[color]?.label || 'Unknown';
  }

  /**
   * Get color based on criticality value
   * @private
   */
  _getCriticalityColor(criticality) {
    if (criticality >= 100) return 'red';
    if (criticality >= 50) return 'orange';
    if (criticality >= 10) return 'yellow';
    return 'green';
  }

  /**
   * Calculate multi-axis criticality score for a cause across all 4 axes
   * Each axis score (1-5) is weighted to create a composite criticality value
   *
   * @param {Object} cause - Cause object with axisScores: {S, O, E, C}
   * @returns {Object} - Composite criticality with breakdown by axis
   */
  calculateMultiAxisCriticality(causes = []) {
    if (!Array.isArray(causes) || causes.length === 0) {
      return [];
    }

    return causes.map(cause => {
      const scores = cause.axisScores || {};
      const safetyScore = Math.max(1, Math.min(5, scores.S || 0));
      const operationalScore = Math.max(1, Math.min(5, scores.O || 0));
      const environmentalScore = Math.max(1, Math.min(5, scores.E || 0));
      const costScore = Math.max(1, Math.min(5, scores.C || 0));

      // Weighted composite: Safety has highest weight (40%), then Operational (30%), Environmental (15%), Cost (15%)
      const compositeCriticality = (safetyScore * 0.40) +
                                   (operationalScore * 0.30) +
                                   (environmentalScore * 0.15) +
                                   (costScore * 0.15);

      return {
        causeId: cause.id,
        causeName: cause.name || cause.description,
        axisScores: {
          S: safetyScore,
          O: operationalScore,
          E: environmentalScore,
          C: costScore
        },
        compositeCriticality: Math.round(compositeCriticality * 100) / 100,
        maxAxisScore: Math.max(safetyScore, operationalScore, environmentalScore, costScore),
        dominantAxis: this._getDominantAxis(safetyScore, operationalScore, environmentalScore, costScore)
      };
    });
  }

  /**
   * Get contributions for a single axis across all causes, sorted by score
   * Used for Pareto analysis per axis
   *
   * @param {Object[]} causes - Array of cause objects
   * @param {string} axis - Axis identifier: 'S', 'O', 'E', or 'C'
   * @returns {Object[]} - Sorted contributions for the specified axis
   */
  getAxisContributions(causes = [], axis = 'S') {
    if (!['S', 'O', 'E', 'C'].includes(axis)) {
      return [];
    }

    return causes
      .map(cause => ({
        causeId: cause.id,
        causeName: cause.name || cause.description,
        axisScore: Math.max(1, Math.min(5, (cause.axisScores || {})[axis] || 0)),
        axis,
        axisLabel: CriticalityEngine.CRITICALITY_AXES[axis].name
      }))
      .sort((a, b) => b.axisScore - a.axisScore);
  }

  /**
   * Get 4-axis heatmap data structure for visualization
   * Returns matrix-like data showing criticality across all axes
   *
   * @param {Object[]} causes - Array of cause objects
   * @returns {Object} - Heatmap data with causes and axis dimensions
   */
  getCriticalityHeatmap(causes = []) {
    const multiAxis = this.calculateMultiAxisCriticality(causes);
    const axes = ['S', 'O', 'E', 'C'];

    const heatmapData = multiAxis.map(item => {
      const row = {
        causeId: item.causeId,
        causeName: item.causeName,
        compositeCriticality: item.compositeCriticality
      };

      axes.forEach(axis => {
        row[axis] = item.axisScores[axis];
        row[`${axis}_color`] = this._getAxisColor(item.axisScores[axis]);
      });

      return row;
    });

    return {
      causes: heatmapData,
      axes: axes.map(a => ({
        id: a,
        label: CriticalityEngine.CRITICALITY_AXES[a].name,
        description: CriticalityEngine.CRITICALITY_AXES[a].description
      })),
      colorScale: {
        1: '#4CAF50', // Green - Low
        2: '#8BC34A', // Light green - Low-Medium
        3: '#FFC107', // Yellow - Medium
        4: '#FF9800', // Orange - Medium-High
        5: '#F44336'  // Red - High
      }
    };
  }

  /**
   * Get top N contributors for a specific axis
   * Useful for focused Pareto analysis and prioritization
   *
   * @param {Object[]} causes - Array of cause objects
   * @param {string} axis - Axis identifier: 'S', 'O', 'E', or 'C'
   * @param {number} topN - Number of top contributors to return
   * @returns {Object[]} - Top N causes for the specified axis
   */
  getTopContributorsByAxis(causes = [], axis = 'S', topN = 10) {
    if (!['S', 'O', 'E', 'C'].includes(axis)) {
      return [];
    }

    const contributions = this.getAxisContributions(causes, axis);
    return contributions.slice(0, Math.max(1, topN));
  }

  /**
   * Determine which axis has the dominant criticality score
   * @private
   */
  _getDominantAxis(safetyScore, operationalScore, environmentalScore, costScore) {
    const scores = {
      S: safetyScore,
      O: operationalScore,
      E: environmentalScore,
      C: costScore
    };

    return Object.entries(scores).reduce((max, [key, value]) =>
      value > max.value ? { key, value } : max
    , { key: 'S', value: 0 }).key;
  }

  /**
   * Get color based on axis score (1-5 scale)
   * @private
   */
  _getAxisColor(score) {
    const normalizedScore = Math.max(1, Math.min(5, score));
    const colorScale = ['#4CAF50', '#8BC34A', '#FFC107', '#FF9800', '#F44336'];
    return colorScale[normalizedScore - 1];
  }
}
